import _ from 'lodash';
import { createSelector } from 'reselect';

import {
  MORTGAGE_INSURANCE_PROVIDER_CATEGORY,
  MORTGAGE_INSURANCE_STRATEGY_PROVIDER_KEY,
} from 'constants/loanActionEditor';

const selectIntegrationStudioState = state => state.system.integrationStudio;

export const selectProviders = createSelector(selectIntegrationStudioState, state =>
  _.values(state.providers || {}),
);

export const selectMortgageInsuranceProviderOptions = createSelector(selectProviders, providers =>
  providers
    .filter(
      ({ key, category }) =>
        category === MORTGAGE_INSURANCE_PROVIDER_CATEGORY &&
        key !== MORTGAGE_INSURANCE_STRATEGY_PROVIDER_KEY,
    )
    .map(({ key, displayName }) => ({ name: displayName || key, value: key })),
);

const selectProvidersMap = createSelector(
  selectIntegrationStudioState,
  state => state.providers || {},
);

const providerByKeyCache = {};

// FH-1939: `settingFieldInherited` (the per-field inherited-vs-overridden provenance map) may be
// absent on a response from an older API version — default it to {} so consumers can safely index
// into it without an `?? {}` at every call site.
const EMPTY_SETTING_FIELD_INHERITED = {};

export const selectProviderByKey = providerKey => {
  if (!providerByKeyCache[providerKey]) {
    providerByKeyCache[providerKey] = createSelector(selectProvidersMap, providersMap => {
      const provider = providersMap[providerKey];

      if (!provider) {
        return null;
      }

      return {
        ...provider,
        settingFieldInherited: provider.settingFieldInherited ?? EMPTY_SETTING_FIELD_INHERITED,
      };
    });
  }

  return providerByKeyCache[providerKey];
};

export const selectProvidersLoading = createSelector(
  selectIntegrationStudioState,
  state => state.providersLoading,
);

export const selectPackages = createSelector(selectIntegrationStudioState, state =>
  _.values(state.packages || {}),
);

export const selectPackagesMap = createSelector(
  selectIntegrationStudioState,
  state => state.packages || {},
);

// Keyed selector cache — avoids creating a new selector instance on every render when called as
// selectPackageById(id) directly inside useSelector.
const packageByIdCache = {};

export const selectPackageById = packageId => {
  if (!packageByIdCache[packageId]) {
    packageByIdCache[packageId] = createSelector(
      selectPackagesMap,
      packagesMap => packagesMap[packageId] || null,
    );
  }

  return packageByIdCache[packageId];
};

const selectMethods = createSelector(selectIntegrationStudioState, state =>
  _.values(state.methods || {}),
);

export const selectMethodsMap = createSelector(
  selectIntegrationStudioState,
  state => state.methods || {},
);

const methodsByPackageIdCache = {};

export const selectMethodsByPackageId = packageId => {
  if (!methodsByPackageIdCache[packageId]) {
    methodsByPackageIdCache[packageId] = createSelector(selectMethods, methods =>
      methods.filter(m => m.packageId === packageId),
    );
  }

  return methodsByPackageIdCache[packageId];
};

const methodByIdCache = {};

export const selectMethodById = methodId => {
  if (!methodByIdCache[methodId]) {
    methodByIdCache[methodId] = createSelector(
      selectMethodsMap,
      methodsMap => methodsMap[methodId] || null,
    );
  }

  return methodByIdCache[methodId];
};

export const selectRunHistory = createSelector(
  selectIntegrationStudioState,
  state => state.runHistory || [],
);

export const selectStats = createSelector(
  selectIntegrationStudioState,
  state => state.stats || null,
);

export const selectPackagesLoading = createSelector(
  selectIntegrationStudioState,
  state => state.packagesLoading,
);

export const selectMethodsLoading = createSelector(
  selectIntegrationStudioState,
  state => state.methodsLoading,
);

export const selectRunHistoryLoading = createSelector(
  selectIntegrationStudioState,
  state => state.runHistoryLoading,
);

const selectMethodRunHistoryMap = createSelector(
  selectIntegrationStudioState,
  state => state.methodRunHistory || {},
);

const EMPTY_RUN_HISTORY = [];

const methodRunHistoryByIdCache = {};

export const selectMethodRunHistory = methodId => {
  if (!methodRunHistoryByIdCache[methodId]) {
    methodRunHistoryByIdCache[methodId] = createSelector(
      selectMethodRunHistoryMap,
      map => map[methodId] ?? EMPTY_RUN_HISTORY,
    );
  }

  return methodRunHistoryByIdCache[methodId];
};

const selectMethodRunHistoryLoadingMap = createSelector(
  selectIntegrationStudioState,
  state => state.methodRunHistoryLoading || {},
);

const methodRunHistoryLoadingByIdCache = {};

export const selectMethodRunHistoryLoading = methodId => {
  if (!methodRunHistoryLoadingByIdCache[methodId]) {
    methodRunHistoryLoadingByIdCache[methodId] = createSelector(
      selectMethodRunHistoryLoadingMap,
      map => !!map[methodId],
    );
  }

  return methodRunHistoryLoadingByIdCache[methodId];
};

const selectMappingsMap = createSelector(
  selectIntegrationStudioState,
  state => state.mappings || {},
);

const mappingsByMethodIdCache = {};

export const selectMappingsByMethodId = methodId => {
  if (!mappingsByMethodIdCache[methodId]) {
    mappingsByMethodIdCache[methodId] = createSelector(
      selectMappingsMap,
      mappingsMap => mappingsMap[methodId] ?? null,
    );
  }

  return mappingsByMethodIdCache[methodId];
};

const selectAllowedHostsMap = createSelector(
  selectIntegrationStudioState,
  state => state.allowedHosts || {},
);

const allowedHostsByPackageIdCache = {};

export const selectAllowedHostsByPackageId = packageId => {
  if (!allowedHostsByPackageIdCache[packageId]) {
    allowedHostsByPackageIdCache[packageId] = createSelector(
      selectAllowedHostsMap,
      map => map[packageId] ?? null,
    );
  }

  return allowedHostsByPackageIdCache[packageId];
};
